import { useState } from 'react';
import { cn } from '@/lib/utils';

/**
 * PatientAvatar — Atom
 *
 * Displays a patient's photo, or initials on a tinted Blue Depth background
 * when no photo is available. Optional VIP ring (green-500) when the patient's
 * `importance.code === 'VIP'` — mirrors Flutter's lemon-green VIP convention,
 * recolored to the Scrips clinical-success token.
 *
 * Palette is intentionally constrained to the Blue Depth + Grey families so
 * the avatar reads as on-brand differentiation, not decorative color. Hash is
 * DJB2 over the full name so per-patient color is stable but evenly distributed
 * (charCodeAt(0) clustered on common first letters).
 */

const PATIENT_PALETTE = [
  { bg: 'var(--color-blue-100)',  fg: 'var(--color-blue-700)' },
  { bg: 'var(--color-blue-200)',  fg: 'var(--color-blue-800)' },
  { bg: 'var(--color-grey-200)',  fg: 'var(--color-grey-700)' },
  { bg: 'var(--color-grey-300)',  fg: 'var(--color-grey-800)' },
] as const;

const PROVIDER_PAIR = {
  bg: 'var(--color-grey-200)',
  fg: 'var(--color-grey-700)',
} as const;

// Initials never go under the 12 px type floor (contrast audit, Antonin 15 Oct): they were 8–9 px in the small discs.
const SIZES = {
  sm: { box: 22, font: 12 },
  md: { box: 26, font: 12 },
  lg: { box: 36, font: 12 },
} as const;

export type PatientAvatarSize = keyof typeof SIZES;
export type PatientAvatarVariant = 'patient' | 'provider';

export interface PatientAvatarProps {
  name: string;
  photoUrl?: string;
  /**
   * Patient importance code from `patient.importance.code` (or wherever
   * the consumer derives the string). `'VIP'` (case-insensitive) adds
   * a green ring.
   *
   * Callers MUST pre-extract `.code` from the importance object —
   * passing the full `{code, display, definition}` object is a bug
   * (`String(obj).toUpperCase()` silently never matches 'VIP'). All
   * three current callers (`view-appointment-popup.tsx`,
   * `day-list-calendar.tsx`, stories) correctly pre-extract; the type
   * here enforces that.
   */
  importance?: string | null;
  variant?: PatientAvatarVariant;
  size?: PatientAvatarSize | number;
  className?: string;
}

function djb2(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function paletteFor(name: string, variant: PatientAvatarVariant) {
  if (variant === 'provider') return PROVIDER_PAIR;
  return PATIENT_PALETTE[djb2(name) % PATIENT_PALETTE.length];
}

function initialsFor(name: string): string {
  // Strip a leading "Dr" title in any of the observed forms:
  //   "Dr. Smith"   → "Smith"
  //   "Dr.Smith"    → "Smith"   (no space — seen in production data)
  //   "Dr Smith"    → "Smith"
  // The dot OR whitespace after "Dr" is required so "Drake Smith" is left alone.
  const cleaned = name.replace(/^Dr(\.\s*|\s+)/i, '');
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return (words[0][0] ?? '').toUpperCase();
  const first = words[0][0] ?? '';
  const last = words[words.length - 1][0] ?? '';
  return (first + last).toUpperCase();
}

export function PatientAvatar({
  name,
  photoUrl,
  importance,
  variant = 'patient',
  size = 'md',
  className,
}: PatientAvatarProps) {
  const [photoFailed, setPhotoFailed] = useState(false);

  const dim = typeof size === 'number' ? { box: size, font: Math.max(12, Math.round(size * 0.36)) } : SIZES[size];
  const { bg, fg } = paletteFor(name, variant);
  const initials = initialsFor(name);
  const isVip = (importance ?? '').toUpperCase() === 'VIP';

  const showPhoto = Boolean(photoUrl) && !photoFailed;

  return (
    <div
      data-vip={isVip || undefined}
      style={{ width: dim.box, height: dim.box }}
      className={cn(
        'relative rounded-full flex-shrink-0',
        isVip && 'ring-2 ring-offset-1 ring-offset-surface-card ring-green-500',
        className,
      )}
    >
      {showPhoto ? (
        <img
          src={photoUrl}
          alt=""
          width={dim.box}
          height={dim.box}
          onError={() => setPhotoFailed(true)}
          className="w-full h-full rounded-full object-cover"
        />
      ) : (
        <div
          aria-label={name}
          style={{ background: bg, color: fg, fontSize: dim.font }}
          className="w-full h-full rounded-full flex items-center justify-center font-bold leading-none select-none"
        >
          {initials}
        </div>
      )}
    </div>
  );
}
